import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useAppDispatch } from './redux';
import {
  getOfflineQueueVersion, listOfflineQueue, subscribeOfflineQueue, OfflineQueueEntry,
} from '../services/offlineQueue';
import { flushOfflineQueue } from '../services/offlineSync';
import { useConnectionStatus } from '../services/connectionStatus';
import { isOfflineCajaDevice } from '../utils/serviceWorker';

/**
 * Lo que la caja hizo sin red y todavía no está en el servidor.
 *
 * Vacía la cola sola en cuanto vuelve la conexión —que es cuando la cajera no está
 * pensando en esto— y publica los contadores para el aviso de la barra y la pantalla
 * "Por revisar".
 */
export function useOfflineQueue() {
  const dispatch = useAppDispatch();
  const connection = useConnectionStatus();
  const version = useSyncExternalStore(subscribeOfflineQueue, getOfflineQueueVersion);
  const [entries, setEntries] = useState<OfflineQueueEntry[]>([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listOfflineQueue().then((list) => { if (!cancelled) setEntries(list); });
    return () => { cancelled = true; };
  }, [version]);

  const sync = useCallback(async () => {
    if (!isOfflineCajaDevice()) return;
    setSyncing(true);
    try {
      await flushOfflineQueue(dispatch);
    } finally {
      setSyncing(false);
    }
  }, [dispatch]);

  // Al volver la conexión, y también al abrir la app (pudo cerrarse con la cola llena).
  useEffect(() => {
    if (connection === 'online') void sync();
  }, [connection, sync]);

  return {
    pending: entries.filter((e) => e.status === 'pending'),
    failed: entries.filter((e) => e.status === 'failed'),
    syncing,
    sync,
  };
}
